/*
 * A `schedule-conflicts` refusal's details → what ConflictsFoundModal renders
 * (R-06-06, R-06-10; catalogue § 3.4).
 *
 * The backend reports the rows of ONE batch per answer (the first batch of the
 * request that has any); `details.batchId` is present only when that batch is a
 * successor, so its absence means the head. Times arrive as IST
 * "YYYY-MM-DD HH:mm:ss" strings, not ISO instants.
 */

import { toDisplayDate, toDisplayTime } from "./timings";

/* The value is an IST wall time. Parsing it into a local Date and back would
   shift it by an hour on the browser's own spring-forward date, so the string is
   split instead. */
const toDisplayMoment = (istDateTime) => {
  const match = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/.exec(istDateTime ?? "");
  return match ? `${toDisplayDate(match?.[1])}, ${toDisplayTime(match?.[2])}` : (istDateTime ?? "");
};

/* The day of the latest clash: the backend's next possible start is the first
   class day after it (R-06-06). The wire strings sort as they read, so the
   latest is the largest — no date is parsed. */
const lastConflictDateOf = (rows) => {
  const latest = (rows ?? []).reduce((max, row) => (row?.at > max ? row?.at : max), "");
  return toDisplayDate(latest?.slice(0, 10)) || null;
};

/**
 * @param {object} details - the refusal's `details`
 * @returns {{ batchId: string|null,
 *             conflicts: {type: string, mentorName: string, at: string, ownClassAt: string,
 *                         classNumber?: number, batchId?: string, studentEmail?: string|null}[],
 *             lastConflictOn: string|null,
 *             nextPossibleStart: string|null, nextPossibleEnd: string|null,
 *             proposedStart: {startDate: string, projectedEnd: string}|null }}
 */
export const conflictsMapper = (details) => ({
  batchId: details?.batchId ?? null,
  conflicts: (details?.conflicts ?? []).map((row) => ({
    type: row?.type,
    mentorName: row?.mentorName ?? "",
    at: toDisplayMoment(row?.at),
    ownClassAt: toDisplayMoment(row?.ownClassAt),
    classNumber: row?.classNumber,
    batchId: row?.batchId,
    studentEmail: row?.studentEmail ?? null,
  })),
  lastConflictOn: lastConflictDateOf(details?.conflicts),
  nextPossibleStart: details?.nextPossibleStart ?? null,
  /* The projected end from that start, kept as the wire's date: the screen
     re-derives a successor's start from it. */
  nextPossibleEnd: details?.projectedEndFromIt ?? null,
  proposedStart: details?.nextPossibleStart
    ? {
        startDate: toDisplayDate(details?.nextPossibleStart),
        projectedEnd: toDisplayDate(details?.projectedEndFromIt),
      }
    : null,
});
